// src/pages/Projects/ProjectTeamMemberList.jsx
// -----------------------------------------------------------------------------
// Component screen managing Project Team Members (Project_Team_Member_TBL)
// Performs full CRUD (Read, Create, Update, Delete) via projectTeamMemberService.
// -----------------------------------------------------------------------------
import { useEffect, useState, useMemo } from 'react';
import projectTeamMemberService from '../../api/projectTeamMemberService';
import projectTeamService from '../../api/projectTeamService';
import userService from '../../api/userService';
import roleService from '../../api/roleService';
import useTableData from '../../hooks/useTableData';
import TableList from '../../components/common/TableList';
import TableForm from '../../components/common/TableForm';
import DeleteConfirmModal from '../../components/common/DeleteConfirmModal';

export default function ProjectTeamMemberList() {
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);

  // Fetch dropdown options on load
  useEffect(() => {
    projectTeamService
      .getAll()
      .then((res) => setTeams(res.data || []))
      .catch(() => {});

    userService
      .getAll()
      .then((res) => setUsers(res.data || []))
      .catch(() => {});

    roleService
      .getAll()
      .then((res) => setRoles(res.data || []))
      .catch(() => {});
  }, []);

  const {
    data,
    paginatedData,
    loading,
    submitting,
    deleting,
    serverErrors,
    searchTerm,
    setSearchTerm,
    sortConfig,
    handleSort,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    totalRecords,
    isFormOpen,
    isDeleteOpen,
    formMode,
    selectedRecord,
    openCreateModal,
    openEditModal,
    closeFormModal,
    openDeleteModal,
    closeDeleteModal,
    handleSave,
    handleDelete,
  } = useTableData(projectTeamMemberService, {
    idKey: 'teamMemberID',
    itemName: 'Project Team Member',
    initialSort: { key: 'teamMemberID', direction: 'asc' },
  });

  // Schema configuration for Project_Team_Member_TBL form fields
  const teamMemberFields = useMemo(() => [
    {
      name: 'teamMemberID',
      label: 'ID',
      type: 'integer',
      isPrimaryKey: true,
      readOnly: true,
    },
    {
      name: 'teamID',
      label: 'Team',
      type: 'select',
      required: true,
      options: teams.map((t) => ({ label: t.teamName, value: t.teamID })),
    },
    {
      name: 'userID',
      label: 'User',
      type: 'select',
      required: true,
      options: users.map((u) => ({
        label: `${u.first_Name || ''} ${u.last_Name || ''}`.trim() || `User #${u.id}`,
        value: u.id,
      })),
    },
    {
      name: 'projectRoleID',
      label: 'Project Role',
      type: 'select',
      required: true,
      options: roles.map((r) => ({ label: r.roleName, value: r.roleID })),
    },
  ], [teams, users, roles]);

  const columns = [
    { key: 'teamMemberID', label: 'ID', sortable: true, width: '80px' },
    {
      key: 'teamID',
      label: 'Team',
      sortable: true,
      render: (row) => {
        const team = teams.find((t) => Number(t.teamID) === Number(row.teamID));
        return team ? team.teamName : `Team ID: ${row.teamID}`;
      },
    },
    {
      key: 'userID',
      label: 'User',
      sortable: true,
      render: (row) => {
        const u = users.find((user) => Number(user.id) === Number(row.userID));
        return u ? `${u.first_Name || ''} ${u.last_Name || ''}`.trim() : `User ID: ${row.userID}`;
      },
    },
    {
      key: 'projectRoleID',
      label: 'Project Role',
      sortable: true,
      render: (row) => {
        const r = roles.find((role) => Number(role.roleID) === Number(row.projectRoleID));
        return r ? r.roleName : `Role ID: ${row.projectRoleID}`;
      },
    },
    {
      key: 'addedDttm',
      label: 'Added Dttm',
      sortable: true,
      render: (row) => (row.addedDttm ? new Date(row.addedDttm).toLocaleString() : '-'),
    },
  ];

  return (
    <div>
      <div className="page-header" style={{ marginBottom: '20px' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '24px' }}>Project Team Members</h1>
          <div className="subtitle" style={{ color: 'var(--color-text-muted)', fontSize: '13px', marginTop: '4px' }}>
            Associate users with specific project teams and define their project-specific roles.
          </div>
        </div>
        <button className="btn btn-primary" onClick={openCreateModal}>
          + Add Team Member
        </button>
      </div>

      {/* Reusable Table List */}
      <TableList
        columns={columns}
        data={data}
        paginatedData={paginatedData}
        loading={loading}
        idKey="teamMemberID"
        emptyMessage="No team members found."
        // Search & Filter
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        // Sorting
        sortConfig={sortConfig}
        onSort={handleSort}
        // Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalRecords={totalRecords}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        // Actions
        onEdit={openEditModal}
        onDelete={openDeleteModal}
      />

      {/* Dynamic Form Modal */}
      {isFormOpen && (
        <TableForm
          fields={teamMemberFields}
          initialValues={selectedRecord || { teamID: '', userID: '', projectRoleID: '' }}
          mode={formMode}
          isOpen={isFormOpen}
          onSubmit={handleSave}
          onCancel={closeFormModal}
          submitting={submitting}
          serverErrors={serverErrors}
          itemName="Project Team Member"
        />
      )}

      {/* Confirmation Delete Modal */}
      <DeleteConfirmModal
        isOpen={isDeleteOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        title="Delete Project Team Member"
        itemLabel={
          selectedRecord
            ? `team member relationship #${selectedRecord.teamMemberID}`
            : 'Project Team Member'
        }
        deleting={deleting}
      />
    </div>
  );
}
